
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { CheckIcon } from '@heroicons/react/solid';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import useLocale from 'next-translate/useTranslation';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { ACTORS } from '@/constants/actors';
import translateReactTree from '@/utils/translateReactTree';
const SectionLabel = ({ children }: {
    children: React.ReactNode;
}) => (<span className='mb-4 inline-block text-xs font-bold uppercase tracking-[0.2em] text-amber-500'>
    {children}
  </span>);
const comparisonRows = [
    { feature: 'Cost per video', ai: '~$1-5', actor: '$500-5,000+' },
    { feature: 'Production time', ai: '2-5 minutes', actor: '1-2 weeks' },
    { feature: 'Languages', ai: '40+ instantly', actor: 'Hire new actors' },
    { feature: 'Consistency', ai: '100% identical', actor: 'Varies' },
    { feature: 'Availability', ai: '24/7', actor: 'Scheduling required' },
    {
        feature: 'Revisions',
        ai: 'Unlimited, instant',
        actor: 'Expensive reshoots',
    },
    {
        feature: 'Scalability',
        ai: 'Unlimited videos',
        actor: 'Limited by budget',
    },
];
const features = [
    {
        title: 'Professional AI Spokespersons',
        description: 'Studio-quality digital presenters that look and sound indistinguishable from real people. Choose from 60+ diverse options.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z'/>
      </svg>),
    },
    {
        title: 'Brand Consistency',
        description: 'Use the same face and voice across hundreds of videos. Your audience builds trust with a familiar, consistent presenter.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z'/>
      </svg>),
    },
    {
        title: 'Multilingual Support',
        description: 'Generate spokesperson videos in 40+ languages with the same presenter. No accent changes, no dubbing artifacts.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802'/>
      </svg>),
    },
    {
        title: 'Script-to-Video',
        description: 'Simply paste your script and watch it transform into a polished spokesperson video. No filming, no editing required.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'/>
      </svg>),
    },
    {
        title: 'Custom Voice Cloning',
        description: 'Clone your brand voice or choose from dozens of natural-sounding voice options to match your corporate identity.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z'/>
      </svg>),
    },
    {
        title: 'HD Video Quality',
        description: 'Every video is rendered in crisp 1080p HD quality, ready for professional use in presentations, ads, and websites.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0118 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0118 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 016 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m0 0h-1.5'/>
      </svg>),
    },
    {
        title: 'Auto Captions',
        description: 'Built-in subtitle generation ensures your message reaches everyone, including viewers watching without sound.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z'/>
      </svg>),
    },
    {
        title: 'Fast Turnaround',
        description: 'Go from script to finished video in minutes, not weeks. Perfect for time-sensitive campaigns and rapid content needs.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z'/>
      </svg>),
    },
    {
        title: 'Enterprise Ready',
        description: 'API access, bulk generation, team collaboration, and priority rendering. Built for businesses that need video at scale.',
        icon: (<svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
        <path strokeLinecap='round' strokeLinejoin='round' d='M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z'/>
      </svg>),
    },
];
const languages = [
    { flag: '\uD83C\uDDFA\uD83C\uDDF8', name: 'English' },
    { flag: '\uD83C\uDDEA\uD83C\uDDF8', name: 'Spanish' },
    { flag: '\uD83C\uDDEB\uD83C\uDDF7', name: 'French' },
    { flag: '\uD83C\uDDE9\uD83C\uDDEA', name: 'German' },
    { flag: '\uD83C\uDDEF\uD83C\uDDF5', name: 'Japanese' },
    { flag: '\uD83C\uDDE7\uD83C\uDDF7', name: 'Portuguese' },
];
const steps = [
    {
        step: '01',
        title: 'Choose Your Spokesperson',
        description: 'Browse our library of 60+ professional AI presenters. Filter by gender, age, ethnicity, and style to find the perfect match for your brand.',
    },
    {
        step: '02',
        title: 'Enter Your Script',
        description: 'Write or paste your message, select the language, and choose a voice. Our AI handles pronunciation, pacing, and natural delivery automatically.',
    },
    {
        step: '03',
        title: 'Generate at Scale',
        description: 'Create unlimited videos with the same spokesperson. Update scripts, change languages, and produce new content in minutes with total consistency.',
    },
];
const useCases = [
    {
        title: 'Sales & Marketing',
        description: 'Create personalized sales pitches, product explainers, and ad creatives at scale. A/B test different scripts with the same presenter.',
        icon: '\uD83D\uDCC8',
    },
    {
        title: 'Employee Training',
        description: 'Standardize onboarding and compliance training across global offices. Update content instantly without reshooting.',
        icon: '\uD83C\uDF93',
    },
    {
        title: 'Customer Onboarding',
        description: 'Welcome new customers with professional walkthrough videos. Localize for every market without additional production costs.',
        icon: '\uD83E\uDD1D',
    },
    {
        title: 'Product Demos',
        description: 'Showcase features and updates with consistent, polished demo videos. Keep your library current as your product evolves.',
        icon: '\uD83D\uDCBB',
    },
    {
        title: 'Investor Relations',
        description: 'Deliver quarterly updates, earnings summaries, and corporate announcements with a professional, on-brand presenter.',
        icon: '\uD83C\uDFE6',
    },
    {
        title: 'Global Communications',
        description: 'Reach every market in their native language. One spokesperson, 40+ languages, zero scheduling conflicts.',
        icon: '\uD83C\uDF0D',
    },
];
const faqs = [
    {
        question: 'What is an AI spokesperson?',
        answer: 'An AI spokesperson is a digital presenter powered by artificial intelligence that can deliver scripted messages in realistic video format. Using advanced deep learning and neural rendering, AI spokespersons replicate natural human speech patterns, facial expressions, and gestures. They look and sound like real people, making them ideal for professional video content at scale.',
    },
    {
        question: 'How much does an AI spokesperson cost compared to hiring actors?',
        answer: 'AI spokespersons cost a fraction of traditional video production. While hiring a real actor typically costs $500-$5,000+ per video (including studio time, crew, and post-production), an AI spokesperson video can be generated for as little as $1-$5. This represents savings of up to 50x, making professional video content accessible to businesses of all sizes.',
    },
    {
        question: 'Can I use the same spokesperson across different languages?',
        answer: "Yes! Our AI maintains the same visual appearance and persona across 40+ languages. The spokesperson's face, expressions, and mannerisms remain consistent while the speech is naturally rendered in the target language. This eliminates the need to hire different actors for each market and ensures your brand maintains a unified presence globally.",
    },
    {
        question: 'Is the quality professional enough for enterprise use?',
        answer: 'Absolutely. Our AI generates HD 1080p video with natural lip-sync, realistic facial expressions, and professional-grade audio. Major enterprises use AI spokespersons for marketing campaigns, internal communications, and customer-facing content. The technology has advanced to the point where viewers often cannot distinguish AI-generated presenters from real actors.',
    },
    {
        question: "Can I customize the spokesperson's voice?",
        answer: "Yes, you can choose from multiple natural-sounding voice options or use our voice cloning feature to match your brand's existing voice talent. Adjust parameters like tone, pace, and emphasis to get the exact delivery you need. Voice cloning requires just a short sample and produces remarkably accurate reproductions.",
    },
    {
        question: 'What industries use AI spokespersons?',
        answer: 'AI spokespersons are used across virtually every industry. Marketing and advertising agencies use them for scalable ad campaigns. Education and e-learning platforms create consistent course content. Healthcare organizations produce patient education materials. Financial services deliver compliance training. Real estate agencies create property walkthroughs. Technology companies produce product demos and tutorials.',
    },
];
const professionalActors = ACTORS.filter((actor) => actor.category === 'professional').slice(0, 12);
const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebApplication',
            name: 'Easy-Peasy.AI Spokesperson Video Creator',
            description: 'Create professional AI spokesperson videos 50x cheaper than hiring actors. 40+ languages, 100% brand consistency.',
            url: 'https://easy-peasy.ai/ai-video-generator/spokesperson',
            applicationCategory: 'MultimediaApplication',
            operatingSystem: 'Web',
            offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
        },
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: 'https://easy-peasy.ai',
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'AI Video Generator',
                    item: 'https://easy-peasy.ai/ai-video-generator',
                },
                {
                    '@type': 'ListItem',
                    position: 3,
                    name: 'AI Spokesperson Video Creator',
                    item: 'https://easy-peasy.ai/ai-video-generator/spokesperson',
                },
            ],
        },
    ],
};
export default function SpokespersonLanding() {
    const { t } = useLocale('ai-video-spokesperson');
    return translateReactTree(<>
      <Seo templateTitle={t('AI Spokesperson Video Creator | Professional AI Video Spokesperson')} description={t('Create professional AI spokesperson videos 50x cheaper than hiring actors. 40+ languages, 100% brand consistency. Enterprise-grade AI video spokesperson for marketing, training & sales.')} image='https://easy-peasy.ai/api/og-template?title=AI%20Spokesperson%20Video%20Creator'/>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}/>
      </Head>

      <div className='relative min-h-screen bg-[#0b0f1a]'>
        {/* ===================== HERO ===================== */}
        <section className='relative overflow-hidden pb-20 '>
          <LandingPageNavigation variant='light'/>

          {/* Noise grain */}
          <div className='pointer-events-none absolute inset-0 z-0 opacity-[0.03]' style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
        }}/>
          {/* Radial glow */}
          <div className='pointer-events-none absolute left-1/2 top-0 z-0 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-amber-500/10 blur-[120px]'/>
          <div className='pointer-events-none absolute right-0 top-1/4 z-0 h-[400px] w-[400px] rounded-full bg-orange-500/5 blur-[100px]'/>

          <div className='relative z-10 mx-auto max-w-7xl mt-20 px-4'>
            <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* Left — text */}
              <div>
                {/* Badge */}
                <div className='mb-8 inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/10 px-4 py-2 text-sm text-amber-400'>
                  <span className='relative flex h-2 w-2'>
                    <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75'/>
                    <span className='relative inline-flex h-2 w-2 rounded-full bg-amber-500'/>
                  </span>
                  Enterprise-Grade AI Spokesperson
                </div>

                {/* H1 */}
                <h1 className='mb-6 text-5xl font-extrabold leading-tight tracking-tight sm:text-6xl lg:text-7xl'>
                  <span className='bg-clip-text text-transparent' style={{
            backgroundImage: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
        }}>
                    AI Spokesperson
                  </span>
                  <br />
                  <span className='text-white'>Video Creator</span>
                </h1>

                <p className='mb-10 max-w-xl text-lg text-slate-400'>
                  Create professional spokesperson videos at scale — 50x cheaper
                  than hiring actors. Same face, same voice, 40+ languages.
                  Enterprise-grade quality in minutes, not weeks.
                </p>

                {/* Stats */}
                <div className='mb-10 grid max-w-lg grid-cols-2 gap-3 sm:grid-cols-4'>
                  {[
            { value: '50x', label: 'Cheaper' },
            { value: '10x', label: 'Faster' },
            { value: '40+', label: 'Languages' },
            { value: '100%', label: 'Consistent' },
        ].map((stat) => (<div key={stat.label} className='rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-4 text-center backdrop-blur-sm'>
                      <div className='text-xl font-bold bg-clip-text text-transparent sm:text-2xl' style={{
                backgroundImage: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
            }}>
                        {stat.value}
                      </div>
                      <div className='mt-1 text-xs text-slate-400'>
                        {stat.label}
                      </div>
                    </div>))}
                </div>

                {/* CTAs */}
                <div className='flex flex-col gap-4 sm:flex-row'>
                  <a href='/ai-talking-videos' className='inline-block rounded-xl bg-amber-500 px-8 py-3 text-base font-semibold text-black shadow-lg shadow-amber-500/25 transition hover:bg-amber-400 hover:scale-[1.03]'>
                    Create AI Spokesperson Video
                  </a>
                  <a href='#how-it-works' className='group inline-flex items-center gap-2 text-sm font-medium text-slate-400 transition hover:text-amber-400'>
                    See how it works
                    <svg className='h-4 w-4 transition group-hover:translate-y-0.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7'/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Right — two videos side by side */}
              <div className='flex justify-center gap-4 lg:justify-end'>
                {[
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b4cc830f-c5ca-435e-8da0-05a0049898aa.mp4',
                label: 'Male Spokesperson',
            },
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b5ef5f5c-cd31-42ef-864b-ddbc3a867a84.mp4',
                label: 'Female Spokesperson',
            },
        ].map((video) => (<div key={video.label} className='relative w-full max-w-[260px] overflow-hidden rounded-2xl border border-white/[0.08] shadow-2xl shadow-amber-500/10'>
                    <div className='relative aspect-[9/16] bg-black'>
                      <video autoPlay muted loop playsInline className='h-full w-full object-cover' src={video.src}>
                        Your browser does not support the video tag.
                      </video>
                      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3'>
                        <p className='text-center text-[11px] font-medium text-white/80'>
                          {video.label}
                        </p>
                      </div>
                    </div>
                  </div>))}
              </div>
            </div>
          </div>
        </section>

        {/* ===================== LOGOS CLOUD ===================== */}
        <section className='relative z-10 py-12'>
          <LogosCloud />
        </section>

        {/* ===================== COST COMPARISON ===================== */}
        <section className='relative py-20'>
          <div className='pointer-events-none absolute inset-0 z-0 opacity-[0.03]' style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
        }}/>
          <div className='relative z-10 mx-auto max-w-5xl px-4'>
            <div className='text-center'>
              <SectionLabel>Cost Comparison</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                AI Spokesperson vs. Hiring Real Actors
              </h2>
              <p className='mx-auto mb-12 max-w-2xl text-slate-400'>
                See why leading companies are switching to AI-powered
                spokesperson videos for their content needs.
              </p>
            </div>

            <div className='overflow-hidden rounded-2xl border border-white/[0.06]'>
              {/* Table header */}
              <div className='grid grid-cols-3 border-b border-white/[0.06] bg-white/[0.03]'>
                <div className='px-6 py-4 text-sm font-semibold text-slate-400'>
                  Feature
                </div>
                <div className='border-l border-white/[0.06] bg-amber-500/5 px-6 py-4 text-center text-sm font-semibold text-amber-400'>
                  AI Spokesperson
                </div>
                <div className='border-l border-white/[0.06] px-6 py-4 text-center text-sm font-semibold text-slate-500'>
                  Real Actor
                </div>
              </div>

              {/* Table rows */}
              {comparisonRows.map((row, i) => (<div key={row.feature} className={`grid grid-cols-3 ${i < comparisonRows.length - 1
                ? 'border-b border-white/[0.06]'
                : ''}`}>
                  <div className='px-6 py-4 text-sm font-medium text-slate-300'>
                    {row.feature}
                  </div>
                  <div className='flex items-center justify-center gap-2 border-l border-white/[0.06] bg-amber-500/5 px-6 py-4 text-center text-sm text-amber-300'>
                    <CheckIcon className='h-4 w-4 flex-shrink-0 text-amber-500'/>
                    {row.ai}
                  </div>
                  <div className='border-l border-white/[0.06] px-6 py-4 text-center text-sm text-slate-500'>
                    {row.actor}
                  </div>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===================== FEATURES ===================== */}
        <section className='relative py-20'>
          <div className='pointer-events-none absolute left-0 top-1/2 z-0 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-amber-500/5 blur-[120px]'/>
          <div className='relative z-10 mx-auto max-w-6xl px-4'>
            <div className='text-center'>
              <SectionLabel>Capabilities</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Everything You Need for AI Spokesperson Videos
              </h2>
              <p className='mx-auto mb-14 max-w-2xl text-slate-400'>
                Professional tools designed for creating spokesperson content at
                enterprise scale.
              </p>
            </div>

            <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {features.map((feature) => (<div key={feature.title} className='group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all hover:border-amber-500/20 hover:bg-white/[0.04]'>
                  <div className='mb-4 inline-flex rounded-xl border border-amber-500/20 bg-amber-500/10 p-3 text-amber-400'>
                    {feature.icon}
                  </div>
                  <h3 className='mb-2 text-lg font-semibold text-white'>
                    {feature.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-slate-400'>
                    {feature.description}
                  </p>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===================== BRAND CONSISTENCY ===================== */}
        <section className='relative py-20'>
          <div className='pointer-events-none absolute right-0 top-0 z-0 h-[600px] w-[600px] translate-x-1/3 rounded-full bg-amber-500/5 blur-[120px]'/>
          <div className='relative z-10 mx-auto max-w-6xl px-4'>
            <div className='grid items-center gap-12 lg:grid-cols-2'>
              {/* Left side */}
              <div>
                <SectionLabel>Brand Consistency</SectionLabel>
                <h2 className='mb-6 text-3xl font-bold text-white sm:text-4xl'>
                  One Face. Every Language.
                  <br />
                  Total Consistency.
                </h2>
                <p className='mb-6 text-slate-400 leading-relaxed'>
                  Your AI spokesperson maintains an identical appearance,
                  mannerisms, and persona across every video you create --
                  regardless of language. Build audience recognition and trust
                  with a familiar face that delivers your message in 40+
                  languages.
                </p>
                <p className='mb-6 text-slate-400 leading-relaxed'>
                  No accent changes. No scheduling conflicts. No inconsistency.
                  Just one professional presenter delivering your brand message
                  to every market in the world.
                </p>
                <ul className='space-y-3'>
                  {[
            'Same facial expressions across all languages',
            'Consistent brand identity worldwide',
            'No dubbing artifacts or lip-sync issues',
            'Instant localization for any market',
        ].map((item) => (<li key={item} className='flex items-center gap-3 text-sm text-slate-300'>
                      <CheckIcon className='h-5 w-5 flex-shrink-0 text-amber-500'/>
                      {item}
                    </li>))}
                </ul>
              </div>

              {/* Right side - Language grid */}
              <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
                {languages.map((lang) => (<div key={lang.name} className='group flex flex-col items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-center transition-all hover:border-amber-500/20 hover:bg-white/[0.04]'>
                    <span className='text-4xl'>{lang.flag}</span>
                    <span className='text-sm font-medium text-slate-300 group-hover:text-amber-400 transition-colors'>
                      {lang.name}
                    </span>
                    <span className='rounded-full bg-amber-500/10 px-3 py-1 text-xs text-amber-400'>
                      Same Presenter
                    </span>
                  </div>))}
              </div>
            </div>
          </div>
        </section>

        {/* ===================== HOW IT WORKS ===================== */}
        <section id='how-it-works' className='relative py-20'>
          <div className='pointer-events-none absolute inset-0 z-0 opacity-[0.03]' style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
        }}/>
          <div className='relative z-10 mx-auto max-w-5xl px-4'>
            <div className='text-center'>
              <SectionLabel>How It Works</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Three Steps to Professional Spokesperson Videos
              </h2>
              <p className='mx-auto mb-14 max-w-2xl text-slate-400'>
                From script to finished video in minutes. No cameras, no
                studios, no actors required.
              </p>
            </div>

            <div className='grid gap-8 md:grid-cols-3'>
              {steps.map((item) => (<div key={item.step} className='relative'>
                  <div className='mb-6 inline-flex items-center justify-center rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-2'>
                    <span className='text-2xl font-bold bg-clip-text text-transparent' style={{
                backgroundImage: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
            }}>
                      {item.step}
                    </span>
                  </div>
                  <h3 className='mb-3 text-xl font-semibold text-white'>
                    {item.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-slate-400'>
                    {item.description}
                  </p>
                </div>))}
            </div>

            <div className='mt-14 text-center'>
              <a href='/ai-talking-videos' className='inline-block rounded-xl bg-amber-500 px-8 py-3 text-base font-semibold text-black shadow-lg shadow-amber-500/25 transition hover:bg-amber-400 hover:scale-[1.03]'>
                Get Started Free
              </a>
            </div>
          </div>
        </section>

        {/* ===================== AI ACTORS ===================== */}
        <section className='relative py-20'>
          <div className='relative z-10 mx-auto max-w-6xl px-4'>
            <div className='text-center'>
              <SectionLabel>AI Presenters</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Professional AI Spokespersons
              </h2>
              <p className='mx-auto mb-14 max-w-2xl text-slate-400'>
                Choose from our library of diverse, studio-quality AI
                presenters. Each one delivers natural speech, realistic
                expressions, and professional presence.
              </p>
            </div>

            <div className='grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
              {professionalActors.map((actor) => (<div key={actor.id} className='group overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] transition-all hover:border-amber-500/20'>
                  <div className='relative aspect-[3/4] overflow-hidden'>
                    <Image src={actor.photo} alt={actor.name} layout='fill' objectFit='cover' className='transition-transform duration-500 group-hover:scale-105'/>
                    <div className='absolute inset-0 bg-gradient-to-t from-[#0b0f1a] via-transparent to-transparent'/>
                    <div className='absolute bottom-0 left-0 right-0 p-4'>
                      <h3 className='text-lg font-semibold text-white'>
                        {actor.name}
                      </h3>
                      {actor.description && (<p className='mt-1 text-xs text-slate-400 line-clamp-2'>
                          {actor.description}
                        </p>)}
                    </div>
                  </div>
                </div>))}
            </div>

            <div className='mt-10 text-center'>
              <a href='/ai-talking-videos' className='inline-block rounded-xl border border-amber-500/30 bg-transparent px-8 py-3 text-base font-semibold text-amber-400 transition hover:bg-amber-500/10'>
                Browse All Spokespersons
              </a>
            </div>
          </div>
        </section>

        {/* ===================== ENTERPRISE USE CASES ===================== */}
        <section className='relative py-20'>
          <div className='pointer-events-none absolute left-1/2 top-1/2 z-0 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/5 blur-[120px]'/>
          <div className='relative z-10 mx-auto max-w-6xl px-4'>
            <div className='text-center'>
              <SectionLabel>Use Cases</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Enterprise Use Cases
              </h2>
              <p className='mx-auto mb-14 max-w-2xl text-slate-400'>
                AI spokesperson videos are transforming how businesses
                communicate across every department and industry.
              </p>
            </div>

            <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {useCases.map((useCase) => (<div key={useCase.title} className='group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all hover:border-amber-500/20 hover:bg-white/[0.04]'>
                  <div className='mb-4 text-3xl'>{useCase.icon}</div>
                  <h3 className='mb-2 text-lg font-semibold text-white'>
                    {useCase.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-slate-400'>
                    {useCase.description}
                  </p>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===================== FAQ ===================== */}
        <section className='relative py-20'>
          <div className='relative z-10 mx-auto max-w-3xl px-4'>
            <div className='text-center'>
              <SectionLabel>FAQ</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Frequently Asked Questions
              </h2>
              <p className='mx-auto mb-14 max-w-xl text-slate-400'>
                Everything you need to know about AI spokesperson video
                creation.
              </p>
            </div>

            <div className='space-y-4'>
              {faqs.map((faq) => (<details key={faq.question} className='group rounded-2xl border border-white/[0.06] bg-white/[0.02] transition-all hover:border-amber-500/10'>
                  <summary className='flex cursor-pointer items-center justify-between px-6 py-5 text-left text-white [&::-webkit-details-marker]:hidden'>
                    <span className='pr-4 font-medium'>{faq.question}</span>
                    <svg className='h-5 w-5 flex-shrink-0 text-amber-500 transition-transform group-open:rotate-180' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7'/>
                    </svg>
                  </summary>
                  <div className='px-6 pb-5 text-sm leading-relaxed text-slate-400'>
                    {faq.answer}
                  </div>
                </details>))}
            </div>
          </div>
        </section>

        {/* ===================== PRICING CTA ===================== */}
        <section className='relative py-20'>
          <div className='mx-auto max-w-4xl px-4'>
            <div className='relative overflow-hidden rounded-3xl p-10 text-center sm:p-16' style={{
            background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(249,115,22,0.1) 50%, rgba(245,158,11,0.05) 100%)',
        }}>
              <div className='absolute inset-0 rounded-3xl border border-amber-500/20'/>
              <div className='pointer-events-none absolute left-1/2 top-0 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/10 blur-[80px]'/>

              <div className='relative z-10'>
                <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                  Transform Your Video Communication
                </h2>
                <p className='mx-auto mb-8 max-w-xl text-slate-400'>
                  Join thousands of businesses using AI spokespersons to scale
                  their video content effortlessly.
                </p>

                <ul className='mx-auto mb-8 flex max-w-md flex-col items-start gap-3 text-left'>
                  {[
            '60+ professional AI presenters',
            '40+ languages supported',
            'HD 1080p video output',
            'Unlimited revisions',
            'No credit card required to start',
        ].map((item) => (<li key={item} className='flex items-center gap-3 text-sm text-slate-300'>
                      <CheckIcon className='h-5 w-5 flex-shrink-0 text-amber-500'/>
                      {item}
                    </li>))}
                </ul>

                <a href='/ai-talking-videos' className='inline-block rounded-xl bg-amber-500 px-8 py-3 text-base font-semibold text-black shadow-lg shadow-amber-500/25 transition hover:bg-amber-400 hover:scale-[1.03]'>
                  Start Creating Free
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== RELATED TOOLS ===================== */}
        <section className='relative py-20'>
          <div className='relative z-10 mx-auto max-w-5xl px-4'>
            <div className='text-center'>
              <SectionLabel>Related Tools</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Explore More AI Video Tools
              </h2>
              <p className='mx-auto mb-12 max-w-xl text-slate-400'>
                Discover our full suite of AI-powered video creation tools.
              </p>
            </div>

            <div className='grid gap-6 sm:grid-cols-3'>
              {[
            {
                title: 'AI Talking Head Videos',
                description: 'Create realistic talking head videos from any photo or AI avatar.',
                href: '/ai-video-generator/talking-head',
            },
            {
                title: 'Make Image Talk',
                description: 'Animate any portrait photo with AI-powered lip-sync and speech.',
                href: '/ai-video-generator/make-image-talk',
            },
            {
                title: 'AI Talking Avatar',
                description: 'Build custom AI avatars that deliver your message with personality.',
                href: '/ai-video-generator/talking-avatar',
            },
        ].map((tool) => (<Link key={tool.href} href={tool.href} className='group block rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all hover:border-amber-500/20 hover:bg-white/[0.04]'>
                  <h3 className='mb-2 text-lg font-semibold text-white group-hover:text-amber-400 transition-colors'>
                    {tool.title}
                  </h3>
                  <p className='text-sm text-slate-400'>{tool.description}</p>
                  <span className='mt-4 inline-flex items-center gap-1 text-sm font-medium text-amber-500'>
                    Learn more
                    <svg className='h-4 w-4 transition group-hover:translate-x-1' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7'/>
                    </svg>
                  </span>
                </Link>))}
            </div>
          </div>
        </section>
      </div>

      {/* ===================== SHARED COMPONENTS (WHITE BG) ===================== */}
      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      <div className='bg-white dark:bg-slate-900'>
        <CallToAction preset='ai-talking-videos'/>
        <Footer />
      </div>
    </>, t);
}
const getStaticProps: GetStaticProps = async () => {
    return {
        props: {},
    };
};

    async function __Next_Translate__getStaticProps__1a0cbfb666e__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/spokesperson',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb666e__ as getStaticProps }
  